import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Tooltip } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import RefreshIcon from '@mui/icons-material/Refresh'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import { helpChat } from '../../../lib/functions/ai'
import {
  ChatHistoryArea,
  ChatInput,
  MessageContainer,
  MessageBubble,
  AiAvatar,
  LoadingBubble,
  LoadingMessageContainer,
  PopperHeader,
  HeaderTitle,
  InputArea,
  SendButton,
  RefreshButton,
} from './styles'

interface ChatMessage {
  text: string
  isUser: boolean
}

const INITIAL_MESSAGE: ChatMessage = {
  text: 'こんにちは！Flagxsの使い方についてご質問があればお気軽にどうぞ。',
  isUser: false,
}

const LoadingMessage = () => (
  <MessageContainer isUser={false}>
    <AiAvatar>
      <SmartToyIcon fontSize="small" />
    </AiAvatar>
    <MessageBubble isUser={false}>
      <LoadingMessageContainer>
        回答を生成中... <LoadingBubble />
      </LoadingMessageContainer>
    </MessageBubble>
  </MessageContainer>
)

export const HelpChatContent: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([INITIAL_MESSAGE])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const chatHistoryRef = useRef<HTMLDivElement>(null)

  const addMessage = useCallback((message: ChatMessage) => {
    setMessages(prev => [...prev, message])
  }, [])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault()
      handleSendMessage()
    }
  }

  const handleRefresh = () => {
    setMessages([INITIAL_MESSAGE])
    setInputValue('')
    setSessionId(undefined)
  }

  const handleSendMessage = async () => {
    if (isLoading) return
    const messageText = inputValue.trim()
    if (!messageText) return

    const userMessage: ChatMessage = {
      text: messageText,
      isUser: true,
    }
    addMessage(userMessage)
    setInputValue('')
    setIsLoading(true)

    try {
      const response = await helpChat({
        query: messageText,
        sessionId,
      })

      const aiResponse: ChatMessage = {
        text: response.text,
        isUser: false,
      }
      addMessage(aiResponse)
      setSessionId(response.sessionId)
    } catch (error) {
      console.error('Error in help chat:', error)
      const errorResponse: ChatMessage = {
        text: '申し訳ありません。エラーが発生しました。もう一度お試しください。',
        isUser: false,
      }
      addMessage(errorResponse)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight
    }
  }, [messages])

  return (
    <>
      <PopperHeader>
        <HeaderTitle>Flagxsヘルプ</HeaderTitle>
        <Tooltip title="会話をリセット">
          <RefreshButton
            size="small"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshIcon fontSize="small" />
          </RefreshButton>
        </Tooltip>
      </PopperHeader>
      <ChatHistoryArea ref={chatHistoryRef}>
        {messages.map((message, index) => (
          <MessageContainer key={`message-${index}`} isUser={message.isUser}>
            {!message.isUser && (
              <AiAvatar>
                <SmartToyIcon fontSize="small" />
              </AiAvatar>
            )}
            <MessageBubble isUser={message.isUser}>
              {message.text}
            </MessageBubble>
          </MessageContainer>
        ))}
        {isLoading && <LoadingMessage />}
      </ChatHistoryArea>
      <InputArea>
        <ChatInput
          placeholder="質問を入力..."
          fullWidth={true}
          inputProps={{ 'aria-label': '質問を入力' }}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
        />
        <Tooltip title="送信">
          <span>
            <SendButton
              onClick={handleSendMessage}
              disabled={isLoading || inputValue.trim() === ''}
            >
              <SendIcon />
            </SendButton>
          </span>
        </Tooltip>
      </InputArea>
    </>
  )
}
